import React, { useState, useEffect } from 'react';
import { getTimetable, addTimetable, deleteTimetable, getClasses, getSubjects, getTeachers } from '../../api';
import { toast } from 'react-toastify';

const Timetable = () => {
  const [timetable, setTimetable] = useState([]);
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    class_id: '', subject_id: '', teacher_id: '',
    day: 'Monday', start_time: '', end_time: '', room_number: ''
  });

  const days = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [tt, c, s, t] = await Promise.all([
        getTimetable(), getClasses(), getSubjects(), getTeachers()
      ]);
      setTimetable(tt.data);
      setClasses(c.data);
      setSubjects(s.data);
      setTeachers(t.data);
    } catch (err) {
      toast.error('Error loading data');
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    try {
      await addTimetable(formData);
      toast.success('Timetable entry added!');
      setShowForm(false);
      setFormData({ class_id: '', subject_id: '', teacher_id: '', day: 'Monday', start_time: '', end_time: '', room_number: '' });
      loadData();
    } catch (err) {
      toast.error('Error adding timetable');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this entry?')) {
      try {
        await deleteTimetable(id);
        toast.success('Deleted!');
        loadData();
      } catch (err) {
        toast.error('Error');
      }
    }
  };

  return (
    <div>
      <div style={styles.header}>
        <h3 style={styles.title}>📅 Timetable Management</h3>
        <button style={styles.addBtn} onClick={() => setShowForm(!showForm)}>
          + Add Entry
        </button>
      </div>

      {showForm && (
        <div style={styles.formCard}>
          <h4 style={styles.formTitle}>Add Timetable Entry</h4>
          <form onSubmit={handleAdd}>
            <div style={styles.formGrid}>
              <div>
                <label style={styles.label}>Class</label>
                <select style={styles.input} value={formData.class_id}
                  onChange={e => setFormData({...formData, class_id: e.target.value})} required>
                  <option value="">Select Class</option>
                  {classes.map(c => (
                    <option key={c.id} value={c.id}>{c.name} {c.section} - {c.group_name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={styles.label}>Subject</label>
                <select style={styles.input} value={formData.subject_id}
                  onChange={e => setFormData({...formData, subject_id: e.target.value})} required>
                  <option value="">Select Subject</option>
                  {subjects.map(s => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={styles.label}>Teacher</label>
                <select style={styles.input} value={formData.teacher_id}
                  onChange={e => setFormData({...formData, teacher_id: e.target.value})} required>
                  <option value="">Select Teacher</option>
                  {teachers.map(t => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={styles.label}>Day</label>
                <select style={styles.input} value={formData.day}
                  onChange={e => setFormData({...formData, day: e.target.value})}>
                  {days.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
              <div>
                <label style={styles.label}>Start Time</label>
                <input style={styles.input} type="time" value={formData.start_time}
                  onChange={e => setFormData({...formData, start_time: e.target.value})} required />
              </div>
              <div>
                <label style={styles.label}>End Time</label>
                <input style={styles.input} type="time" value={formData.end_time}
                  onChange={e => setFormData({...formData, end_time: e.target.value})} required />
              </div>
              <div>
                <label style={styles.label}>Room Number</label>
                <input style={styles.input} placeholder="e.g. Room 5"
                  value={formData.room_number}
                  onChange={e => setFormData({...formData, room_number: e.target.value})} />
              </div>
            </div>
            <div style={styles.btnGroup}>
              <button type="submit" style={styles.addBtn}>Save</button>
              <button type="button" style={styles.cancelBtn}
                onClick={() => setShowForm(false)}>Cancel</button>
            </div>
          </form>
        </div>
      )}

      {days.map(day => {
        const dayEntries = timetable.filter(t => t.day === day);
        if (dayEntries.length === 0) return null;
        return (
          <div key={day} style={styles.dayCard}>
            <h4 style={styles.dayTitle}>{day}</h4>
            <table style={styles.table}>
              <thead>
                <tr style={styles.tableHeader}>
                  <th style={styles.th}>Time</th>
                  <th style={styles.th}>Subject</th>
                  <th style={styles.th}>Teacher</th>
                  <th style={styles.th}>Class</th>
                  <th style={styles.th}>Room</th>
                  <th style={styles.th}>Action</th>
                </tr>
              </thead>
              <tbody>
                {dayEntries.map(entry => (
                  <tr key={entry.id} style={styles.tableRow}>
                    <td style={styles.td}>
                      {entry.start_time} - {entry.end_time}
                    </td>
                    <td style={styles.td}>{entry.subject_name}</td>
                    <td style={styles.td}>{entry.teacher_name}</td>
                    <td style={styles.td}>{entry.class_name}</td>
                    <td style={styles.td}>{entry.room_number || '-'}</td>
                    <td style={styles.td}>
                      <button style={styles.deleteBtn}
                        onClick={() => handleDelete(entry.id)}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })}
    </div>
  );
};

const styles = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' },
  title: { color: '#1a3a5c', margin: 0 },
  addBtn: { padding: '10px 20px', background: '#0d6efd', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' },
  cancelBtn: { padding: '10px 20px', background: '#6c757d', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' },
  formCard: { background: 'white', borderRadius: '12px', padding: '20px', marginBottom: '20px', boxShadow: '0 2px 10px rgba(0,0,0,0.08)' },
  formTitle: { color: '#1a3a5c', margin: '0 0 15px' },
  formGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', marginBottom: '15px' },
  input: { padding: '10px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px', width: '100%', marginTop: '5px' },
  label: { fontSize: '13px', fontWeight: '600', color: '#555' },
  btnGroup: { display: 'flex', gap: '10px' },
  dayCard: { background: 'white', borderRadius: '12px', padding: '20px', marginBottom: '15px', boxShadow: '0 2px 10px rgba(0,0,0,0.08)' },
  dayTitle: { color: '#1a3a5c', margin: '0 0 15px', fontSize: '16px', borderBottom: '2px solid #0d6efd', paddingBottom: '8px' },
  table: { width: '100%', borderCollapse: 'collapse' },
  tableHeader: { background: '#f5f7fa' },
  th: { padding: '10px 12px', color: '#555', textAlign: 'left', fontSize: '13px', fontWeight: '600' },
  tableRow: { borderBottom: '1px solid #eee' },
  td: { padding: '10px 12px', fontSize: '13px', color: '#444' },
  deleteBtn: { padding: '5px 10px', background: '#dc3545', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '12px' },
};

export default Timetable;